// src/components/LembreteSessaoConfigModal.jsx

import React, { useCallback, useEffect, useMemo, useState } from "react";

import "../sass/pages/lembreteSessaoConfig.scss";

const API_BASE = (
  process.env.REACT_APP_API_URL ||
  "https://api.psyassist.com.br"
).replace(/\/$/, "");

const AUTH_KEYS = {
  TOKEN: "psy_token",
  USER_ID: "psy_id",
};

const MAX_ETAPAS = 10;

const readAuth = (key) =>
  sessionStorage.getItem(key) || localStorage.getItem(key) || "";

const saveAuthToken = (token) => {
  if (!token) return;

  localStorage.setItem(AUTH_KEYS.TOKEN, token);
  sessionStorage.setItem(AUTH_KEYS.TOKEN, token);
};

function createLocalId(index = 0) {
  return `etapa_${Date.now()}_${index}_${Math.random()
    .toString(36)
    .slice(2, 8)}`;
}

function novaEtapa(index) {
  return {
    id: createLocalId(index),
    ordem: index + 1,
    quantidade: 1,
    unidade: "HORAS",
    antecedenciaMinutos: 60,
    assunto: "Lembrete de sessão agendada",
    mensagem: "Este é um lembrete da sua próxima sessão.",
    ativa: true,
  };
}

function paraTela(etapa, index) {
  const minutosOriginais = Number(etapa?.antecedenciaMinutos);
  const minutos =
    Number.isFinite(minutosOriginais) && minutosOriginais > 0
      ? minutosOriginais
      : 60;

  let quantidade = minutos;
  let unidade = "MINUTOS";

  if (minutos % 1440 === 0) {
    quantidade = minutos / 1440;
    unidade = "DIAS";
  } else if (minutos % 60 === 0) {
    quantidade = minutos / 60;
    unidade = "HORAS";
  }

  return {
    ...etapa,
    id: String(etapa?.id || etapa?._id || createLocalId(index)),
    ordem: Number(etapa?.ordem || index + 1),
    quantidade,
    unidade,
    antecedenciaMinutos: minutos,
    assunto: String(etapa?.assunto || "Lembrete de sessão agendada"),
    mensagem: String(
      etapa?.mensagem || "Este é um lembrete da sua próxima sessão."
    ),
    ativa: etapa?.ativa !== false,
  };
}

function paraMinutos(quantidade, unidade) {
  const valor = Number(quantidade);

  if (!Number.isFinite(valor) || valor <= 0) {
    return 0;
  }

  if (unidade === "DIAS") return Math.round(valor * 1440);
  if (unidade === "HORAS") return Math.round(valor * 60);

  return Math.round(valor);
}

function normalizarConfiguracao(data) {
  const value = data?.configuracao || data?.data || data || {};
  const etapasRecebidas = Array.isArray(value?.etapas) ? value.etapas : [];

  return {
    ...value,
    ativa: value?.ativa !== false,
    enviarParaCliente: value?.enviarParaCliente !== false,
    enviarParaPsicologo: value?.enviarParaPsicologo !== false,
    pararQuandoConfirmado: value?.pararQuandoConfirmado === true,
    etapas: etapasRecebidas.map(paraTela),
  };
}

export default function LembreteSessaoConfigModal({
  open,
  onClose,
  onSaved,
}) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const request = useCallback(async (path, options = {}) => {
    const token = readAuth(AUTH_KEYS.TOKEN);
    const userId = readAuth(AUTH_KEYS.USER_ID);

    if (!token || !userId) {
      throw new Error("Sua sessão expirou. Faça login novamente.");
    }

    const separator = path.includes("?") ? "&" : "?";
    const url = `${API_BASE}${path}${separator}id=${encodeURIComponent(userId)}`;

    const response = await fetch(url, {
      ...options,
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        ...(options.headers || {}),
      },
    });

    saveAuthToken(response.headers.get("x-new-token"));

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw new Error(
        data?.message ||
          data?.error ||
          "Falha na comunicação com a API."
      );
    }

    return data;
  }, []);

  const carregarConfiguracao = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const data = await request("/psy/lembrete-sessao/configuracao");
      setConfig(normalizarConfiguracao(data));
    } catch (err) {
      setConfig(null);
      setError(err?.message || "Não foi possível carregar a configuração.");
    } finally {
      setLoading(false);
    }
  }, [request]);

  useEffect(() => {
    if (!open) return;

    carregarConfiguracao();
  }, [open, carregarConfiguracao]);

  useEffect(() => {
    if (!open) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape" && !saving) {
        onClose();
      }
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, onClose, saving]);

  const etapasAtivas = useMemo(
    () => config?.etapas?.filter((etapa) => etapa.ativa !== false).length || 0,
    [config]
  );

  if (!open) return null;

  const updateConfig = (patch) => {
    setConfig((current) => ({
      ...current,
      ...patch,
    }));
  };

  const updateEtapa = (index, patch) => {
    setConfig((current) => ({
      ...current,
      etapas: current.etapas.map((item, itemIndex) =>
        itemIndex === index ? { ...item, ...patch } : item
      ),
    }));
  };

  const removerEtapa = (index) => {
    setConfig((current) => ({
      ...current,
      etapas: current.etapas
        .filter((_, itemIndex) => itemIndex !== index)
        .map((item, itemIndex) => ({
          ...item,
          ordem: itemIndex + 1,
        })),
    }));
  };

  const adicionarEtapa = () => {
    setError("");

    setConfig((current) => {
      if (current.etapas.length >= MAX_ETAPAS) {
        setError(`É permitido cadastrar no máximo ${MAX_ETAPAS} etapas.`);
        return current;
      }

      return {
        ...current,
        etapas: [
          ...current.etapas,
          novaEtapa(current.etapas.length),
        ],
      };
    });
  };

  const validar = () => {
    if (!config) {
      return "A configuração ainda não foi carregada.";
    }

    if (!Array.isArray(config.etapas) || config.etapas.length === 0) {
      return "Adicione pelo menos uma etapa de lembrete.";
    }

    if (config.etapas.length > MAX_ETAPAS) {
      return `É permitido cadastrar no máximo ${MAX_ETAPAS} etapas.`;
    }

    for (let index = 0; index < config.etapas.length; index += 1) {
      const etapa = config.etapas[index];
      const minutos = paraMinutos(etapa.quantidade, etapa.unidade);

      if (minutos <= 0) {
        return `Informe uma antecedência válida na etapa ${index + 1}.`;
      }

      if (!String(etapa.assunto || "").trim()) {
        return `Informe o assunto da etapa ${index + 1}.`;
      }

      if (!String(etapa.mensagem || "").trim()) {
        return `Informe a mensagem da etapa ${index + 1}.`;
      }
    }

    return "";
  };

  const salvar = async () => {
    const validationError = validar();

    if (validationError) {
      setError(validationError);
      return;
    }

    setSaving(true);
    setError("");

    try {
      const payload = {
        ativa: config.ativa !== false,
        enviarParaCliente: config.enviarParaCliente !== false,
        enviarParaPsicologo: config.enviarParaPsicologo !== false,
        pararQuandoConfirmado: config.pararQuandoConfirmado === true,
        etapas: config.etapas.map((item, index) => ({
          id: String(item.id || item._id || `etapa_${index + 1}`),
          ordem: index + 1,
          antecedenciaMinutos: paraMinutos(
            item.quantidade,
            item.unidade
          ),
          assunto: String(item.assunto || "").trim(),
          mensagem: String(item.mensagem || "").trim(),
          ativa: item.ativa !== false,
        })),
      };

      const response = await request(
        "/psy/lembrete-sessao/configuracao",
        {
          method: "PUT",
          body: JSON.stringify(payload),
        }
      );

      setConfig(normalizarConfiguracao(response));
      onSaved?.(response);
      onClose();
    } catch (err) {
      setError(err?.message || "Não foi possível salvar a configuração.");
    } finally {
      setSaving(false);
    }
  };

  const handleBackdropMouseDown = (event) => {
    if (event.target === event.currentTarget && !saving) {
      onClose();
    }
  };

  return (
    <div
      className="ls-modal-backdrop"
      role="presentation"
      onMouseDown={handleBackdropMouseDown}
    >
      <section
        className="ls-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ls-modal-title"
        aria-describedby="ls-modal-description"
      >
        <header className="ls-modal__head">
          <div>
            <span>Automação da agenda</span>
            <strong id="ls-modal-title">Lembretes de sessão</strong>
            <small id="ls-modal-description">
              Defina quando paciente e psicólogo receberão os avisos.
            </small>
          </div>

          <button
            className="ls-modal__close"
            type="button"
            onClick={onClose}
            disabled={saving}
            aria-label="Fechar configuração"
          >
            ×
          </button>
        </header>

        {loading ? (
          <div className="ls-loading" role="status">
            <span className="ls-loading__spinner" aria-hidden="true" />
            Carregando configuração...
          </div>
        ) : null}

        {error ? (
          <div className="ls-error" role="alert">
            <strong>Atenção</strong>
            <span>{error}</span>

            {!config && !loading ? (
              <button type="button" onClick={carregarConfiguracao}>
                Tentar novamente
              </button>
            ) : null}
          </div>
        ) : null}

        {config && !loading ? (
          <div className="ls-modal__body">
            <section className="ls-summary">
              <div>
                <span>Status da régua</span>
                <strong>{config.ativa !== false ? "Ativa" : "Desativada"}</strong>
              </div>

              <div>
                <span>Etapas cadastradas</span>
                <strong>{config.etapas.length}</strong>
              </div>

              <div>
                <span>Etapas ativas</span>
                <strong>{etapasAtivas}</strong>
              </div>
            </section>

            <section className="ls-options" aria-label="Opções dos lembretes">
              <label className="ls-toggle">
                <input
                  type="checkbox"
                  checked={config.ativa !== false}
                  onChange={(event) =>
                    updateConfig({ ativa: event.target.checked })
                  }
                />
                <span className="ls-toggle__control" aria-hidden="true" />
                <span>
                  <strong>Régua ativa</strong>
                  <small>Permite o agendamento automático dos lembretes.</small>
                </span>
              </label>

              <label className="ls-toggle">
                <input
                  type="checkbox"
                  checked={config.enviarParaCliente !== false}
                  onChange={(event) =>
                    updateConfig({
                      enviarParaCliente: event.target.checked,
                    })
                  }
                />
                <span className="ls-toggle__control" aria-hidden="true" />
                <span>
                  <strong>Enviar ao paciente</strong>
                  <small>O paciente receberá os avisos no e-mail cadastrado.</small>
                </span>
              </label>

              <label className="ls-toggle">
                <input
                  type="checkbox"
                  checked={config.enviarParaPsicologo !== false}
                  onChange={(event) =>
                    updateConfig({
                      enviarParaPsicologo: event.target.checked,
                    })
                  }
                />
                <span className="ls-toggle__control" aria-hidden="true" />
                <span>
                  <strong>Enviar ao psicólogo</strong>
                  <small>O profissional também receberá os lembretes.</small>
                </span>
              </label>

              <label className="ls-toggle">
                <input
                  type="checkbox"
                  checked={config.pararQuandoConfirmado === true}
                  onChange={(event) =>
                    updateConfig({
                      pararQuandoConfirmado: event.target.checked,
                    })
                  }
                />
                <span className="ls-toggle__control" aria-hidden="true" />
                <span>
                  <strong>Parar após confirmação</strong>
                  <small>
                    Etapas futuras poderão ser ignoradas quando houver confirmação.
                  </small>
                </span>
              </label>
            </section>

            <div className="ls-section-title">
              <div>
                <span>Sequência automática</span>
                <strong>Etapas dos lembretes</strong>
              </div>

              <small>
                {config.etapas.length}/{MAX_ETAPAS} etapas
              </small>
            </div>

            <div className="ls-etapas">
              {config.etapas.map((etapa, index) => (
                <article
                  className={`ls-etapa ${
                    etapa.ativa === false ? "ls-etapa--inactive" : ""
                  }`}
                  key={etapa.id || etapa._id || index}
                >
                  <div className="ls-etapa__top">
                    <div className="ls-etapa__number">{index + 1}</div>

                    <div className="ls-etapa__title">
                      <strong>Etapa {index + 1}</strong>
                      <span>
                        Envio automático antes do horário da sessão.
                      </span>
                    </div>

                    <label className="ls-etapa__active">
                      <input
                        type="checkbox"
                        checked={etapa.ativa !== false}
                        onChange={(event) =>
                          updateEtapa(index, {
                            ativa: event.target.checked,
                          })
                        }
                      />
                      Ativa
                    </label>

                    <button
                      className="ls-etapa__remove"
                      type="button"
                      onClick={() => removerEtapa(index)}
                      disabled={config.etapas.length === 1}
                      title={
                        config.etapas.length === 1
                          ? "É necessário manter pelo menos uma etapa."
                          : "Remover etapa"
                      }
                    >
                      Remover
                    </button>
                  </div>

                  <div className="ls-grid">
                    <label>
                      <span>Antecedência</span>
                      <input
                        type="number"
                        min="1"
                        step="1"
                        value={etapa.quantidade}
                        onChange={(event) =>
                          updateEtapa(index, {
                            quantidade: event.target.value,
                          })
                        }
                      />
                    </label>

                    <label>
                      <span>Unidade</span>
                      <select
                        value={etapa.unidade}
                        onChange={(event) =>
                          updateEtapa(index, {
                            unidade: event.target.value,
                          })
                        }
                      >
                        <option value="MINUTOS">minutos antes</option>
                        <option value="HORAS">horas antes</option>
                        <option value="DIAS">dias antes</option>
                      </select>
                    </label>
                  </div>

                  <label className="ls-field">
                    <span>Assunto do e-mail</span>
                    <input
                      value={etapa.assunto}
                      maxLength={160}
                      onChange={(event) =>
                        updateEtapa(index, {
                          assunto: event.target.value,
                        })
                      }
                      placeholder="Ex.: Lembrete de sessão agendada"
                    />
                  </label>

                  <label className="ls-field">
                    <span>Mensagem personalizada</span>
                    <textarea
                      rows="4"
                      value={etapa.mensagem}
                      maxLength={2000}
                      onChange={(event) =>
                        updateEtapa(index, {
                          mensagem: event.target.value,
                        })
                      }
                      placeholder="Digite a mensagem que acompanhará o lembrete."
                    />
                    <small>{String(etapa.mensagem || "").length}/2000</small>
                  </label>
                </article>
              ))}
            </div>

            <button
              className="ls-add"
              type="button"
              onClick={adicionarEtapa}
              disabled={config.etapas.length >= MAX_ETAPAS}
            >
              <span aria-hidden="true">+</span>
              {config.etapas.length >= MAX_ETAPAS
                ? "Limite de etapas atingido"
                : "Adicionar etapa"}
            </button>
          </div>
        ) : null}

        <footer className="ls-modal__actions">
          <button
            className="ls-secondary"
            type="button"
            onClick={onClose}
            disabled={saving}
          >
            Cancelar
          </button>

          <button
            className="ls-primary"
            type="button"
            onClick={salvar}
            disabled={saving || loading || !config}
          >
            {saving ? "Salvando..." : "Salvar configuração"}
          </button>
        </footer>
      </section>
    </div>
  );
}
